import { useCallback } from "react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { logError } from "../lib/logError";

// ─── Types ───────────────────────────────────────────────────
export type AuditAction =
  | "project.create"
  | "project.update"
  | "project.delete"
  | "member.invite"
  | "member.remove"
  | "member.role_change"
  | "integration.create"
  | "integration.update"
  | "integration.delete"
  | "org.settings_update"
  | "org.branding_update"
  | "subscription.upgrade"
  | "subscription.cancel"
  | "subscription.activate"
  | "subscription.suspend"
  | "ai.credit_limit_reached"
  | "export.pdf"
  | "export.report";

interface AuditEntry {
  action: AuditAction;
  entity_type?: string;
  entity_id?: string;
  metadata?: Record<string, unknown>;
}

// ─── Hook ────────────────────────────────────────────────────
export function useAuditLog() {
  const orgId = requireOrganizationId();

  /**
   * Log an action to the audit trail.
   * Non-blocking: errors are logged but don't interrupt UX.
   */
  const log = useCallback(
    async (entry: AuditEntry) => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase.from as any)("audit_log").insert({
          organization_id: orgId,
          user_id: user?.id ?? null,
          action: entry.action,
          entity_type: entry.entity_type ?? null,
          entity_id: entry.entity_id ?? null,
          metadata: entry.metadata ?? {},
        });

        if (error) {
          logError("[Audit] Failed to log:", error);
        }
      } catch (err) {
        // Silent fail: audit logging must never break UX
        logError("[Audit] Error:", err);
      }
    },
    [orgId]
  );

  return { log };
}
